import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ensureInterFontFace } from '../inter-font';

interface ShadowEscapeProps {
    children: React.ReactNode;
    className?: string;
}

const FORM_STYLES_ID = 'malachite-form-styles';

// Form styles as a string - this ensures it works in all build environments
const formStyles = `
/* Malachite Form Styles - for components outside Shadow DOM */

.malachite-form-container {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
}

.malachite-form {
  display: flex;
  flex-direction: column;
}

.malachite-form .space-y-4 > * + * {
  margin-top: 1rem;
}

/* Basic input styling that matches Tailwind defaults */
.malachite-form input,
.malachite-form textarea,
.malachite-form select {
  appearance: none;
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #111827;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  width: 100%;
}

.malachite-form input:focus,
.malachite-form textarea:focus,
.malachite-form select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.malachite-form input:disabled,
.malachite-form textarea:disabled,
.malachite-form select:disabled {
  background-color: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}

.malachite-form button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  border-radius: 0.375rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.malachite-form button[type="submit"] {
  background-color: #3b82f6;
  color: #ffffff;
}

.malachite-form button[type="submit"]:hover {
  background-color: #2563eb;
}

.malachite-form button[type="button"] {
  background-color: #ffffff;
  color: #374151;
  border-color: #d1d5db;
}

.malachite-form button[type="button"]:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.malachite-form button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .malachite-form-container {
    color: #f3f4f6;
  }
  
  .malachite-form input,
  .malachite-form textarea,
  .malachite-form select {
    background-color: #1f2937;
    border-color: #374151;
    color: #f3f4f6;
  }
  
  .malachite-form input:focus,
  .malachite-form textarea:focus,
  .malachite-form select:focus {
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
  }
  
  .malachite-form button[type="button"] {
    background-color: #374151;
    color: #f3f4f6;
    border-color: #4b5563;
  }
  
  .malachite-form button[type="button"]:hover {
    background-color: #4b5563;
  }
}
`;

/**
 * Adds the escaped-form stylesheet to <head> unless it is already there. The check reads
 * the DOM, not a module-level latch: a latch goes stale the moment anything removes the
 * <style> (a host app clearing <head> between routes, a test resetting the document), and
 * the styles would then never be re-added.
 */
const ensureFormStyles = (): void => {
    if (typeof document === 'undefined' || !document.head) {
        return;
    }
    if (document.getElementById(FORM_STYLES_ID)) {
        return;
    }
    const styleElement = document.createElement('style');
    styleElement.id = FORM_STYLES_ID;
    styleElement.textContent = formStyles;
    document.head.appendChild(styleElement);
};

/**
 * ShadowEscape renders its children OUTSIDE the Shadow DOM to avoid React hook issues.
 * This is used internally by form components to escape Shadow DOM boundaries.
 * 
 * The component finds the nearest non-shadow DOM ancestor and renders there.
 */
export const ShadowEscape: React.FC<ShadowEscapeProps> = ({ children, className = '' }) => {
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
    const markerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Inter @font-face must live in the document so escaped (non-shadow) forms can use it.
        ensureInterFontFace();

        ensureFormStyles();

        if (markerRef.current) {
            // Find the closest non-shadow DOM ancestor
            let element: Node | null = markerRef.current;
            let isInShadowDOM = false;

            // Traverse up the DOM tree to find a non-shadow root container
            while (element) {
                const rootNode = element.getRootNode();

                // Check if we're in a shadow root
                if (rootNode !== document) {
                    isInShadowDOM = true;
                    // We're in a shadow root, find its host
                    const shadowRoot = rootNode as ShadowRoot;
                    element = shadowRoot.host;
                } else {
                    // We found a regular DOM element, use its parent
                    const parentElement = element.parentElement || document.body;

                    if (isInShadowDOM) {
                        // Create a container for the escaped content
                        const container = document.createElement('div');
                        container.className = `malachite-escape-container ${className}`;
                        container.style.cssText = `
                            position: relative;
                            z-index: 1000;
                        `;

                        parentElement.appendChild(container);
                        setPortalContainer(container);

                        // Cleanup function
                        return () => {
                            if (container.parentElement) {
                                container.parentElement.removeChild(container);
                            }
                        };
                    }
                    // We're not in Shadow DOM, just render normally
                    setPortalContainer(parentElement);
                    break;
                }
            }
        }
    }, [className]);

    if (!portalContainer) {
        // Render a marker in Shadow DOM while we find the escape container
        return <div ref={markerRef} style={{ display: 'none' }} />;
    }

    // Render children outside Shadow DOM
    return (
        <>
            <div ref={markerRef} style={{ display: 'none' }} />
            {createPortal(children, portalContainer)}
        </>
    );
};

export default ShadowEscape; 